import ExportDefinitionDetailDTO from "features/exports/dtos/ExportDefinitionDetailDTO";
import FormFieldDescriptor from "features/shared/forms/dynamicForm/models/FormFieldDescriptor";
import { isEmpty } from "features/shared/helpers/objects";
import { useEffect, useState } from "react";
import ValidationRule from "features/validation/rules/ValidationRule";
import ExportDefinitionParametrDTO from "features/exports/dtos/ExportDefinitionParametrDTO";
import { FormExportParametersMap } from "features/exports/models/FormExportParametersMap";
import FormExportParameterConfig from "features/exports/models/FormExportParameterConfig";
import { ExportDefinitionParametrDataType } from "features/exports/enums/exportDefinitionParametrDataType";
import OptionsFieldDescriptor from "features/shared/forms/dynamicForm/models/OptionsFieldDescriptor";
import {
  FormFields,
  SetFormValueFunction,
} from "features/shared/forms/hooks/useForm";
import { useSelector } from "react-redux";
import IAppState from "infrastructure/store/IAppState";
import { UserState } from "redux-oidc";
import CustomerState from "infrastructure/store/customer/CustomerState";
import AdminUserState from "infrastructure/store/adminUser/AdminUserState";
import {
  customerParamName,
  exportFileTypeKey,
  customerParamEmail,
} from "features/exports/helpers/exportFormHelper";

// models
export interface ExportDynamicFormHookProps {
  exportDefinition: ExportDefinitionDetailDTO;
  setFormValue: SetFormValueFunction;
  paramNamesToSkip?: string[];
}

export interface ExportDynamicFormHookResult {
  formJson: string;
}

// hook
export const useExportDynamicForm = ({
  exportDefinition,
  setFormValue,
  paramNamesToSkip = [],
}: ExportDynamicFormHookProps): ExportDynamicFormHookResult => {
  // store
  const customer = useSelector<IAppState, CustomerState>(
    (store) => store.customer
  ).customer;
  const user = useSelector<IAppState, UserState>(
    (store) => store.oidc
  ).user;
  const adminUser = useSelector<IAppState, AdminUserState>(
  (store) => (store as any).adminUser
  );

  // state
  const [formJson, setFormJson] = useState<string>("[]"); // empty JSON array

  // side-effects
  useEffect(() => {
    // create dynamic form from report definition
    if (!isEmpty(exportDefinition)) {
      const descriptors = getFormFieldsAndSetDefaults();
      const descriptorsJson = JSON.stringify(descriptors, regexReplacer);

      setFormJson(descriptorsJson);
    }

    // clear
    return () => {
      setFormJson("[]");
    };
  }, [exportDefinition]);

  // helpers
  const getFormFieldsAndSetDefaults = (): FormFieldDescriptor[] => {
    let descriptors: FormFieldDescriptor[] = [];

    // parameters
    if (exportDefinition.parameters) {
      for (const param of exportDefinition.parameters) {
        // not render internal parameters
        if (paramNamesToSkip.includes(param.name!)) {
          // set customer id parameter
          if (param.name === customerParamName) {
            setFormValue(
              param.exportDefinitionParametrId!,
              customer.customerId
            );
          }
          // set email parameter
          if (param.name === customerParamEmail) {
            const emailForExport = adminUser?.isSelectedUser && adminUser?.email ? adminUser.email: user?.profile?.email;
            setFormValue(
              param.exportDefinitionParametrId!,
              emailForExport
            );
          }

          continue;
        }

        const paramConfig = FormExportParametersMap[param.dataType!];

        let descriptor: FormFieldDescriptor = {
          name: param.exportDefinitionParametrId!,
          label: param.name!,
          type: paramConfig.type,
          validation: getDescriptorValidation(param, paramConfig),
          options: getDescriptorOptions(param),
          className: "col-lg-6 px-2",
        };
        // set default value for options field
        setOptionsFieldDefaults(param);
        // add descriptor to render
        descriptors.push(descriptor);
      }
    }
    // file type
    if (exportDefinition.filesType && exportDefinition.filesType.length > 1) {
      const fileTypeDesc = getFileTypeDescriptor();
      descriptors.push(fileTypeDesc);
    } else if (exportDefinition.filesType?.length === 1) {
      // if is only one file type set internal (not show fields)
      setFormValue(
        exportFileTypeKey,
        exportDefinition.filesType[0].exportDefinitionFileTypeId
      );
    }

    // return
    return descriptors;
  };

  const getDescriptorValidation = (
    param: ExportDefinitionParametrDTO,
    config: FormExportParameterConfig
  ): ValidationRule | undefined => {
    const configValidation = config.validation;

    if (configValidation !== undefined) {
      configValidation.required = param.isRequired;
      return configValidation;
    }
    if (param.isRequired) {
      return {
        type: "OnlyRequired",
        required: true,
      };
    }

    return undefined;
  };

  const getDescriptorOptions = (
    param: ExportDefinitionParametrDTO
  ): OptionsFieldDescriptor | undefined => {
    const enumTypes: ExportDefinitionParametrDataType[] = ["Enum", "EnumMulti"];

    if (enumTypes.includes(param.dataType!)) {
      let optionsDesc: OptionsFieldDescriptor = {
        source: "external",
        options: param.enumValues ?? [],
        optionName: "name",
        optionValue: "code",
      };

      return optionsDesc;
    }

    return undefined;
  };

  const setOptionsFieldDefaults = (param: ExportDefinitionParametrDTO) => {
    if (param.dataType === "Enum") {
      const defaultValue = param.enumValues?.find((x) => x.isDefault);
      if (defaultValue) {
        setFormValue(param.exportDefinitionParametrId!, defaultValue.code);
      }
    } else if (param.dataType === "EnumMulti") {
      const defaultValues = param.enumValues?.filter((x) => x.isDefault);
      if (defaultValues) {
        setFormValue(
          param.exportDefinitionParametrId!,
          defaultValues.map((x) => String(x.code))
        );
      }
    }
  };

  const getFileTypeDescriptor = (): FormFieldDescriptor => {
    return {
      name: exportFileTypeKey,
      label: "Typ souboru",
      translationPrefix: "exportDynamicForm",
      type: "SelectBox",
      validation: { type: "OnlyRequired", required: true },
      options: {
        source: "options",
        options: exportDefinition.filesType,
        optionName: "name",
        optionValue: "exportDefinitionFileTypeId",
      },
      className: "col-lg-6 px-2",
    };
  };

  // JSON stringify function
  function regexReplacer(this: any, key: string, value: any): any {
    if (value instanceof RegExp) {
      return value.source;
    }
    return value;
  }

  // result
  return {
    formJson: formJson,
  };
};
